<template>
  <!--
    Plain <img> rather than <NuxtImg>: @nuxt/image transforms through IPX, which needs a
    server. The Capacitor build is static and runs from capacitor://localhost, so the /_ipx/
    URLs never resolved and every image through this component failed to load — the perks page
    being where it was spotted.

    Contentful sources still get their format parameter via imageUrl below, so the webp
    negotiation that mattered is kept; what is given up is IPX resizing on web.
  -->
  <img
    v-if="isLazyLoad"
    :id="generateUID('image')"
    ref="imageX"
    class="lazyload"
    v-bind="{
      ...$attrs,
      src: imageUrl,
      alt: $attrs.alt || 'Image'
    }"
    :data-src="imageUrl"
    :data-expand="expand"
    :data-testid="dataTestid"
    loading="lazy"
  />

  <img
    v-else
    :id="generateUID('image')"
    ref="imageX"
    v-bind="{
      ...$attrs,
      src: imageUrl,
      alt: $attrs.alt || 'Image'
    }"
    :data-expand="expand"
    :data-testid="dataTestid"
    loading="eager"
  />
</template>

<script setup>
import { getContentfulImageExt } from '~/composables/webp-checker'

defineOptions({
  name: 'AtomsImage'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  src: {
    type: [String, Object],
    required: true
  },

  expand: {
    type: [String, Number],
    default: -20
  },

  preload: {
    type: Boolean,
    default: false
  },

  imageLazyLoad: {
    type: Boolean,
    default: false
  }
})

const $attrs = useAttrs()

const imageX = ref()
const isLazyLoad = ref()

onMounted(() => {
  // The ref is now the element itself, not a component instance — there is no $el to reach
  // through.
  if (!imageX.value) return

  isLazyLoad.value = props.imageLazyLoad
    ? props.imageLazyLoad
    : !isInViewport(imageX.value)
})

// computed so the fm= param follows the webp-support probe result
// (imageExt flips to png on browsers without WebP support); the src prop
// also allows objects, which getContentfulImageExt can't handle
const imageUrl = computed(() => typeof props.src === 'string' ? getContentfulImageExt(props.src) : props.src)
</script>
